import type { AlertColor } from "@mui/material";
import { Alert, Container, Box, Button, Snackbar, Grid, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useParams, useRevalidator } from "react-router-dom";

import CertificateSignatureModal from "./CertificateSignatureModal";
import ChooseSignatureTypeModal from "./ChooseSignatureTypeModal";
import Documents from "./documents";
import Info from "./info";
import LinkCodeHasExpired from "./LinkCodeHasExpired";
import NamirialSignatureModal from "./NamirialSignatureModal";
import RefuseSignatureModal from "./RefuseSignatureModal";
import TokenSignatureModal from "./TokenSignatureModal";
import urlToBase64 from "../../../utils/urlToBase64";

type ModalType = "choose" | "token" | "certificate" | "namirial" | null;

function Result(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { hash } = useParams<{ hash: string }>();
  const signerData = useLoaderData() as ResponseSignatures;
  const revalidator = useRevalidator();

  const [documentSelected, setDocumentSelected] = useState<FileSigner>(
    {} as FileSigner,
  );
  const [messageAlert, setMessageAlert] = useState("");
  const [typeAlert, setTypeAlert] = useState<AlertColor>("info");
  const [openAlert, setOpenAlert] = useState(false);
  const [modal, setModal] = useState<ModalType>(null);
  const [openRefuseModal, setOpenRefuseModal] = useState(false);
  const [signatureType, setSignatureType] = useState<"0" | "1" | "2" | "3">(
    "0",
  );
  const [isSigningCompleted, setIsSigningCompleted] = useState(false);

  const currentSigner = signerData?.signers?.find(
    (signer) => signer.id === signerData?.currentSignerId,
  );
  const signerIdentifier = currentSigner?.email || currentSigner?.document || "";

  const reloader = useCallback((): void => {
    revalidator.revalidate();
  }, [revalidator]);

  const handleSignatureSuccess = useCallback((): void => {
    setIsSigningCompleted(true);
    reloader();
  }, [reloader]);

  useEffect(() => {
    const currentSigner = signerData?.signers?.find(
      (signer) => signer.id === signerData?.currentSignerId,
    );

    if (currentSigner?.signatureType === "3" && i18n.language !== "es") {
      void i18n.changeLanguage("es");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n
  }, [signerData?.signers, signerData?.currentSignerId]);

  useEffect(() => {
    if (signerData?.files && signerData.files.length > 0) {
      setDocumentSelected(signerData.files[0] || ({} as FileSigner));
    }

    const firstDocumentUrl = signerData?.files?.[0]?.temporaryUrl;
    if (firstDocumentUrl) {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = firstDocumentUrl;
      link.as = "fetch";
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [signerData?.files]);

  const openChooseModal = (): void => {
    setModal("choose");
  };
  const openTokenModal = (): void => {
    setModal("token");
  };
  const openCertificateModal = (): void => {
    setModal("certificate");
  };
  const openNamirialModal = (): void => {
    setModal("namirial");
  };
  const closeModal = (): void => {
    setModal(null);
  };

  const handleOpenModal = (): void => {
    if (signerData?.signers && signerData.signers.length > 0) {
      const currentSigner = signerData.signers.find(
        (signer) => signer.id === signerData.currentSignerId,
      );
      if (!currentSigner) return;
      setSignatureType(currentSigner.signatureType as "0" | "1" | "2" | "3");
      switch (currentSigner.signatureType) {
        case "0":
          openTokenModal();
          break;
        case "1":
          openCertificateModal();
          break;
        case "2":
          openChooseModal();
          break;
        case "3":
          openNamirialModal();
          break;
        default:
          openChooseModal();
          break;
      }
    }
  };

  const handleViewDocument = (doc: FileSigner): void => {
    setDocumentSelected(doc);
  };

  const handleClose = (): void => {
    setOpenAlert(false);
  };

  const handleCloseWindow = (): void => {
    const newWindow = window.open("", "_self");
    newWindow?.close();
  };

  if (signerData?.hasExpired) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Container>
          <LinkCodeHasExpired info={signerData.objects} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Container>
        <Grid container spacing={3} sx={{ minHeight: "85vh" }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Info
              authorName={signerData?.authorName || ""}
              createdAt={signerData?.createdAt || ""}
              id={signerData?.id || ""}
              status={signerData?.status || ""}
              title={signerData?.title || ""}
              updatedAt={signerData?.updatedAt || ""}
            />

            <Documents
              documentSelectedId={documentSelected.id || ""}
              documents={signerData?.files || []}
              handleViewDocument={handleViewDocument}
            />

            {(() => {
              const currentSigner = signerData?.signers?.find(
                (signer) => signer.id === signerData?.currentSignerId,
              );
                const isRefused = currentSigner?.status === "REJECTED";

                if (isRefused) {
                  return (
                    <Alert severity="error" sx={{ mt: 4, borderRadius: "12px" }}>
                      <Typography fontWeight="bold" variant="subtitle2">
                        {t("sign.refused.title", "Assinatura recusada")}
                      </Typography>
                      <Typography sx={{ mt: 0.5 }} variant="body2">
                        {t(
                          "sign.refused.message",
                          "Você recusou a assinatura deste documento.",
                        )}
                      </Typography>
                      {(currentSigner?.refusalReason ||
                        currentSigner?.motivo) && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            bgcolor: "rgba(0, 0, 0, 0.04)",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            color="text.secondary"
                            display="block"
                            fontWeight="bold"
                            variant="caption"
                          >
                            {t("sign.refused.reasonLabel", "Motivo da recusa:")}
                          </Typography>
                          <Typography
                            sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                            variant="body2"
                          >
                            {currentSigner.refusalReason ||
                              currentSigner.motivo}
                          </Typography>
                        </Box>
                      )}
                      {currentSigner?.refusedAt && (
                        <Typography
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                          variant="caption"
                        >
                          {t("sign.refused.dateLabel", "Data da recusa:")}{" "}
                          {dayjs(currentSigner.refusedAt).format(
                            "DD/MM/YYYY [às] HH:mm:ss",
                          )}
                        </Typography>
                      )}
                    </Alert>
                  );
                }

                const currentOrder = currentSigner?.signatureOrder;
                const hasPendingPrecedingSigners =
                  currentOrder !== undefined &&
                  currentOrder !== null &&
                  signerData?.signers?.some(
                    (s) =>
                      s.id !== currentSigner?.id &&
                      s.signatureOrder !== undefined &&
                      s.signatureOrder !== null &&
                      s.signatureOrder < currentOrder &&
                      s.status !== "SIGNED",
                  );

                if (signerData?.status !== "SIGNED") {
                  return (
                    <Stack direction="column" spacing={2} sx={{ mt: 6 }}>
                      {hasPendingPrecedingSigners && (
                        <Alert severity="info" sx={{ borderRadius: "12px" }}>
                          <Typography variant="body2">
                            {t(
                              "sign.info.awaitingPrecedingSigners",
                              "Aguardando os assinantes anteriores, conforme a ordem de assinatura.",
                            )}
                          </Typography>
                        </Alert>
                      )}
                      <Button
                        color="primary"
                        disabled={
                          currentSigner?.status !== "PENDING" ||
                          isSigningCompleted ||
                          hasPendingPrecedingSigners
                        }
                        onClick={handleOpenModal}
                        style={{ width: "100%", borderRadius: "12px" }}
                        variant="contained"
                      >
                        {t("sign.info.signButton")}
                      </Button>
                      <Button
                        color="error"
                        disabled={
                          currentSigner?.status !== "PENDING" ||
                          isSigningCompleted
                        }
                        onClick={() => {
                          setOpenRefuseModal(true);
                        }}
                        style={{ width: "100%", borderRadius: "12px" }}
                        variant="outlined"
                      >
                        {t("sign.info.refuseButton", "Recusar assinatura")}
                      </Button>
                      <Button
                        color="primary"
                        disabled={
                          currentSigner?.status !== "PENDING" ||
                          isSigningCompleted
                        }
                        onClick={handleCloseWindow}
                        style={{ width: "100%", borderRadius: "12px" }}
                        variant="outlined"
                      >
                        {t("sign.info.signLater")}
                      </Button>
                    </Stack>
                  );
                }

                return null;
              })()}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <iframe
                height="100%"
                src={`https://pdf-viewer.smartdocs.com.br/web/viewer.html?file=${urlToBase64(
                  documentSelected.temporaryUrl || "",
                )}&pdfBug=disable&externalLinkTarget=0`}
                style={{
                  border: "none",
                }}
                title={documentSelected.filename || "Document Viewer"}
                width="100%"
              />
            </Grid>
          </Grid>

        <ChooseSignatureTypeModal
          id={signerData?.id || ""}
          signerIdentifier={signerIdentifier}
          onClose={closeModal}
          onSelectCertificate={openCertificateModal}
          onSelectToken={openTokenModal}
          onSuccess={handleSignatureSuccess}
          open={modal === "choose"}
          setMessageAlert={setMessageAlert}
          setOpenAlert={setOpenAlert}
          setTypeAlert={setTypeAlert}
        />

        <TokenSignatureModal
          id={signerData?.id || ""}
          signerIdentifier={signerIdentifier}
          onBack={() => {
            setModal("choose");
          }}
          onClose={closeModal}
          onSuccess={handleSignatureSuccess}
          open={modal === "token"}
          setMessageAlert={setMessageAlert}
          setOpenAlert={setOpenAlert}
          setTypeAlert={setTypeAlert}
          signatureType={signatureType}
        />

        <CertificateSignatureModal
          id={signerData?.id || ""}
          onBack={() => {
            setModal("choose");
          }}
          onClose={closeModal}
          onSuccess={handleSignatureSuccess}
          open={modal === "certificate"}
          setMessageAlert={setMessageAlert}
          setOpenAlert={setOpenAlert}
          setTypeAlert={setTypeAlert}
          signatureType={signatureType}
        />

        <NamirialSignatureModal
          id={signerData?.id || ""}
          onClose={closeModal}
          onSuccess={handleSignatureSuccess}
          open={modal === "namirial"}
          setMessageAlert={setMessageAlert}
          setOpenAlert={setOpenAlert}
          setTypeAlert={setTypeAlert}
        />

        <RefuseSignatureModal
          id={signerData?.id || ""}
          linkCode={hash || ""}
          linkId={hash || ""}
          onClose={() => {
            setOpenRefuseModal(false);
          }}
          onSuccess={handleSignatureSuccess}
          open={openRefuseModal}
          setMessageAlert={setMessageAlert}
          setOpenAlert={setOpenAlert}
          setTypeAlert={setTypeAlert}
        />

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={5000}
          onClose={handleClose}
          open={openAlert}
        >
          <Alert
            onClose={handleClose}
            severity={typeAlert}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {messageAlert}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default Result;
